import Column from '@app/components/layout/Column'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { Tabs } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { Overview } from './tabContent/Overview'
import { Events } from './tabContent/Events'
import { Initiatives } from './tabContent/Initiatives'
import { DevelopmentAreas } from './tabContent/DevelopmentAreas'
import { Concepts } from './tabContent/Concepts'
import useMediaQuery from '@app/util/useMediaQuery'
import { useParamState } from '@app/util/hooks/useParamState'
import { ServiceProviderViewModel } from 'functions/app/service-provider/view'
import {
  FEATURE_TOGGLE_IDENTIFIERS,
  FeatureToggleIdentifier,
} from '@schema/featureToggle'

import { Contact } from './Contact'

const tabs = {
  ...FEATURE_TOGGLE_IDENTIFIERS,
  contact: 'contact',
} as const

export const TabContent = ({
  serviceProvider,
}: {
  serviceProvider: ServiceProviderViewModel
}) => {
  const [language, _] = useLanguage()
  const appTranslations = i18n.languages[language].app
  const translations = appTranslations.serviceProviderPage
  const [tab, setTab] = useParamState(tabs, FEATURE_TOGGLE_IDENTIFIERS.overview)
  const isMobile = useMediaQuery()
  const paddingRight = isMobile ? '0rem' : '4.75rem'

  const isFeatureEnabled = (identifier: FeatureToggleIdentifier) => {
    const matchingToggles = serviceProvider.featureToggles?.filter(
      (ft) => ft.identifier === identifier,
    )
    return matchingToggles?.some((ft) => ft.enabled) ?? false
  }

  return (
    <Column
      alignItems="stretch"
      gap={isMobile ? '1rem' : '2rem'}
      style={{ paddingRight: paddingRight }}
    >
      <Tabs.Group selectedTab={tab}>
        {isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.overview) && (
          <Tabs.Tab
            label={translations.overview}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.overview}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.events) && (
          <Tabs.Tab
            label={translations.events}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.events}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.developmentArea) && (
          <Tabs.Tab
            label={translations.developmentArea}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.developmentArea}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.concepts) && (
          <Tabs.Tab
            label={translations.concepts}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.concepts}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.initiatives) && (
          <Tabs.Tab
            label={translations.initiatives}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.initiatives}
            onSelectTab={setTab}
          />
        )}
        {isMobile && (
          <Tabs.Tab
            label={appTranslations.common.contact}
            tabKey={tabs.contact}
            onSelectTab={setTab}
          />
        )}
      </Tabs.Group>
      {match(tab)
        .with(FEATURE_TOGGLE_IDENTIFIERS.overview, () =>
          isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.overview) ? (
            <Overview
              key={FEATURE_TOGGLE_IDENTIFIERS.overview}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.events, () =>
          isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.events) ? (
            <Events
              key={FEATURE_TOGGLE_IDENTIFIERS.events}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.developmentArea, () =>
          isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.developmentArea) ? (
            <DevelopmentAreas
              key={FEATURE_TOGGLE_IDENTIFIERS.developmentArea}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.concepts, () =>
          isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.concepts) ? (
            <Concepts
              key={FEATURE_TOGGLE_IDENTIFIERS.concepts}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.initiatives, () =>
          isFeatureEnabled(FEATURE_TOGGLE_IDENTIFIERS.initiatives) ? (
            <Initiatives
              key={FEATURE_TOGGLE_IDENTIFIERS.initiatives}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(tabs.contact, () => (
          <Contact key={tabs.contact} serviceProvider={serviceProvider} />
        ))
        .exhaustive()}
    </Column>
  )
}
